import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { CommonModalService, DateUtilService } from 'gung-common';
import { AuthService, BackendInterceptor, BaseViewConfigService, GungError, GungModalService, Order, PriceConfigService, ProductAssortmentParentService, ProductService } from 'gung-standard';
import { JeevesOrderActionConfigService, JeevesOrderDetailsComponent, JeevesOrderDetailsConfigService, JeevesOrderService } from 'gung-standard-jeeves';
import { catchError, first, map, switchMap, throwError } from 'rxjs';

@Component({
    selector: 'otto-olsen-order-details',
    templateUrl: './otto-olsen-order-details.component.html',
    styleUrls: ['./otto-olsen-order-details.component.scss'],
    standalone: false
})
export class OttoOlsenOrderDetailsComponent extends JeevesOrderDetailsComponent implements OnInit {
  pageTitle = '';
  public hidePrice: boolean;
  totalPrice: number;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  private titleService = inject(Title);
  private location = inject(Location);
  private router = inject(Router)

  constructor() {
    super();
    this.priceConfigService.hidePrice.subscribe(hidePrice => (this.hidePrice = hidePrice));
  }

  ngOnInit() {
    this.authService.getRoles().subscribe(roles => {
      const filteredRoles = roles.filter(role => role.toUpperCase() === 'SALES');

      if (filteredRoles.length === 0) {
        return;
      }
      this.isSales = true;
    });

    this.route.params.pipe(
      map(params => {
        return (params.orderId as string);
      }),
      switchMap(orderId => this.orderService.getOrder(orderId, true).pipe(
        first(),
        catchError((err: HttpErrorResponse) => {
          const message = err.error?.gungMessage || err.error?.message || 'ERROR';
          const gungError: GungError = {
            ...new Error(message),
            i18nKeyMessage: message
          };

          this.commonModalService.openGungErrorHandler(gungError);

          return throwError(() => new Error(message));
        }))
      )
    ).subscribe(order => {
      const mappedOrder = this.mapOrder(order);
      mappedOrder.rows = mappedOrder.rows.filter(row => row.extra.orp.ordradnrstrpos === 0 && row.extra.orp.ordradst !== 90);
      this.order = mappedOrder;
      const productIds = mappedOrder.rows.map(row => row.productId);
      this.totalPrice = mappedOrder.rows.reduce((total, row) => total + row.extra.orp.vbordradsum, 0);
      this.pageTitle = this.translateService.instant('OTTO_OLSEN_ORDER_DETAIS', {
        order: order.id,
        customerOrderId: order.extra.oh.kundbestnr || 'No number'
      });
      this.titleService.setTitle(this.pageTitle);

      this.productService.getProductsByIds(productIds).subscribe(products => {
        this.products = {};
        products.forEach(product => {
          this.products[product.id] = product;
        });
      });
    });
  }

  protected mapOrder(order: Order): Order {
    return order;
  }

  navigateBackwards() {
    if (document?.referrer) {
      this.location.back();
    } else {
      // this.router.navigate(['/orders']);
      this.router.navigate(['/customers', this.order.billingCustomerId],
        {
          queryParams: {
            tab: 'orders'
          }
        });
    }
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.order.rows.sort((a, b) => {
      const aValue = this.getColumnValue(a, column);
      const bValue = this.getColumnValue(b, column);

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'string') {
        return this.sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return this.sortDirection === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    });
  }

  getSortIconClass(column: string, ascending: boolean = true): string {
    if (this.sortColumn !== column) {
      return 'sort-icon inactive';
    }

    const isActive = this.sortDirection === (ascending ? 'asc' : 'desc');
    return isActive ? 'sort-icon active' : 'sort-icon inactive';
  }

  getColumnValue(row: any, column: string): any {
    switch (column) {
      case 'rowNumber': return row.extra.orp.ordradnr;
      case 'productId': return row.productId;
      case 'name': return this.products?.[row.productId]?.name;
      case 'deliveryDate': return new Date(row.extra.orp.ordberlevdat);
      case 'quantity': return row.quantity;
      case 'delivered': return row.extra.orp.ordlevantal;
      case 'backorder': return row.extra.orp.ordrestant;
      case 'unit': return row.extra.orp.enhetskod;
      case 'unitPrice': return row.extra.orp.vb_pris;
      case 'discount': return row.extra.orp.rabatt1;
      case 'totalPrice': return row.extra.orp.vbordradsum;
      default: return '';
    }
  }
}
